import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { memo, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import { MedicalRecordContext, ProfileContext } from 'context';
import { useAction } from 'hooks';
import BloodGlucoseProfileAgpMiniChart from 'components/Charts/BloodGlucoseProfileAgpMiniChart';
import * as actions from '../../actions';
import * as selectors from '../../selectors';
import styles from './DailyGlucoseProfile.pcss';
const DaysSelector = ({ isInProgress, onDatesChange })=>{
    const profileContext = useContext(ProfileContext);
    const medicalRecord = useContext(MedicalRecordContext);
    const { dataType } = medicalRecord;
    const bgmReadings = useSelector(profileContext.selectors.readings);
    const cgmReadings = useSelector(profileContext.selectors.cgmReadings);
    const readings = dataType === 'CGM' ? cgmReadings : bgmReadings;
    const dailyRecordsPaginatedByMonth = useSelector(selectors.dailyRecordsPaginatedByMonth);
    const highlightedDates = useSelector(selectors.highlightedDates);
    const selectHighlightedDates = useAction(actions.selectHighlightedDates);
    const [focus, setFocus] = useState(false);
    const [hover, setHover] = useState(null);
    const dailyRecordsLen = dailyRecordsPaginatedByMonth ? dailyRecordsPaginatedByMonth.length : 0;
    let startMoment;
    let endMoment;
    let startDay;
    let daysBeforeMonth = [];
    if (dailyRecordsLen) {
        startMoment = moment.utc(dailyRecordsPaginatedByMonth[0].date).startOf('day');
        endMoment = moment.utc(dailyRecordsPaginatedByMonth[dailyRecordsLen - 1].date).endOf('day');
        startDay = startMoment.day() - 1;
        if (startDay > 0) {
            daysBeforeMonth = new Array(startDay).fill({});
        } else if (startDay === -1) {
            daysBeforeMonth = new Array(6).fill({});
        }
    }
    const readingsByDays = useMemo(()=>readings.reduce((acc, reading)=>{
            if (!startMoment || !endMoment) {
                return acc;
            }
            const startTS = +startMoment.locale('en').format('X');
            const endTS = +endMoment.locale('en').format('X');
            if (reading.timestamp >= startTS && reading.timestamp <= endTS) {
                const readingDate = moment.unix(reading.timestamp).utc().format('YYYY-MM-DD');
                if (!acc[readingDate]) {
                    acc[readingDate] = [];
                }
                acc[readingDate].push(reading);
            }
            return acc;
        }, {}), [
        readings,
        dailyRecordsPaginatedByMonth
    ]);
    if (!dailyRecordsLen) {
        return null;
    }
    const onSelectHighlightedDates = (range)=>{
        selectHighlightedDates(range);
        if (focus) {
            onDatesChange(moment.utc(range.start).startOf('day'), moment.utc(range.end).endOf('day'));
        }
    };
    const onDayClick = (dailyPreview)=>{
        if (focus) {
            if (highlightedDates.length === 1 && highlightedDates[0] === dailyPreview.date) {
                setFocus(false);
                return;
            }
            if (highlightedDates.length === 1 && highlightedDates[0] !== dailyPreview.date) {
                const start = highlightedDates[0];
                const end = dailyPreview.date;
                const range = {
                    start: start < end ? start : end,
                    end: end < start ? start : end
                };
                onSelectHighlightedDates(range);
                setFocus(false);
                return;
            }
        }
        onSelectHighlightedDates({
            start: dailyPreview.date,
            end: dailyPreview.date
        });
        setFocus(true);
    };
    return /*#__PURE__*/ _jsx("div", {
        className: styles.daySelector,
        children: map([
            ...daysBeforeMonth,
            ...dailyRecordsPaginatedByMonth
        ], (dailyPreview, idx)=>{
            const dayReadings = readingsByDays[dailyPreview.date];
            const isActive = highlightedDates.indexOf(dailyPreview.date) !== -1;
            const isDisabled = false;
            const isHover = focus && highlightedDates.length === 1 && (dailyPreview.date <= highlightedDates[0] && dailyPreview.date >= hover || dailyPreview.date >= highlightedDates[0] && dailyPreview.date <= hover);
            const onClick = (evt)=>{
                evt.stopPropagation();
                onDayClick(dailyPreview);
            };
            const onHover = ()=>{
                setHover(dailyPreview.date);
            };
            return /*#__PURE__*/ _jsx("div", {
                className: cn(styles.daySelector__cell, {
                    [styles['daySelector__cell--hover']]: isHover,
                    [styles['daySelector__cell--active']]: isActive,
                    [styles['daySelector__cell--empty']]: isEmpty(dailyPreview),
                    fadingLoader: isInProgress
                }),
                onClick: onClick,
                role: "presentation",
                onMouseEnter: onHover,
                children: !isEmpty(dailyPreview) && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.dayNumber,
                            children: dailyPreview.day
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.daySelector__cellChartWrapper,
                            children: !isDisabled ? /*#__PURE__*/ _jsx(BloodGlucoseProfileAgpMiniChart, {
                                records: dayReadings,
                                isInProgress: isInProgress
                            }, dailyPreview.day) : /*#__PURE__*/ _jsx("div", {
                                className: styles.daySelector__placeholder
                            })
                        })
                    ]
                })
            }, `${idx}-dgp-cell`);
        })
    });
};
export default /*#__PURE__*/ memo(DaysSelector);
